import {
	Component,
	OnInit,
	Input,
	EventEmitter,
	Output,
	AfterContentInit,
	OnDestroy,
	ViewChild,
	ElementRef,
	OnChanges,
	SimpleChanges,
	AfterViewInit,
	ViewEncapsulation,
	ContentChildren,
	QueryList
} from '@angular/core';
import { IdService } from '../../shared/services/id.service';
import { SidenavItemComponent } from './sidenav-item/sidenav-item.component';
import { Subscription } from 'rxjs';
import { ActivatedRouteSnapshot } from '@angular/router';
import { SidenavService } from './sidenav.service';
import { IInputTextUpdateEvent } from '../input/input.types';
import { ISidenavEvent } from './sidenav.types';

@Component({
	selector: 'rbc-side-nav',
	templateUrl: './sidenav.component.html',
	styleUrls: ['./sidenav.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [SidenavService]
})
export class SidenavComponent implements OnInit, OnDestroy, OnChanges, AfterContentInit, AfterViewInit {
	nspace = 'rbc-side-nav';
	@Input() id: string;
	@Input() arrows: boolean = true;
	@Input() currentPage: ActivatedRouteSnapshot;
	@Input() hamburgerPosition: 'left' | 'right' = 'left';
	_navIsToggled: boolean = false;
	@Input()
	get navIsToggled(): boolean {
		return this._navIsToggled;
	}
	set navIsToggled(val: boolean) {
		this._navIsToggled = val;
		this.hamburgerIsToggled = this.navIsToggled;
		this.toggleLinks(this.hamburgerIsToggled);
	}
	@Input() search: boolean = false;
	@Input() searchPlaceholder: string = 'Search';
	@Input() showHamburger: boolean = true;
	@Input() toggle: 'icon' | 'full' = 'full';

	@Output() onSearch: EventEmitter<IInputTextUpdateEvent> = new EventEmitter<IInputTextUpdateEvent>();
	@Output() navtoggleupdate: EventEmitter<ISidenavEvent> = new EventEmitter<ISidenavEvent>();

	@ViewChild('sideNav') sideNav: ElementRef;
	@ViewChild('mobileTopSlot') mobileTopSlot: ElementRef;
	@ViewChild('otherSlot') otherSlot: ElementRef;
	@ViewChild('mobileBottomSlot') mobileBottomSlot: ElementRef;

	@ContentChildren(SidenavItemComponent) items: QueryList<SidenavItemComponent>;

	get isDisabledNavigation(): boolean {
		const isMd = window.matchMedia('(max-width: 991px)').matches;
		const checkDisabled =
			(!isMd && this.toggle && this.toggle !== 'icon' && this.hamburgerIsToggled) ||
			(isMd && !this.hamburgerIsToggled);
		return checkDisabled;
	}

	_hamburgerIsToggled: boolean = false;
	get hamburgerIsToggled(): boolean {
		return this._hamburgerIsToggled;
	}
	set hamburgerIsToggled(val: boolean) {
		this._hamburgerIsToggled = val;
		// toggle nav if body is clicked on smaller screens
		if (window.matchMedia('(max-width: 991px)').matches) {
			// attach some handlers and invert control of their detachment
			const bodyClick = ['click', 'touchend'].map(eventName => {
				const handler = this.toggleLinksMobile.bind(this);
				document.body.addEventListener(eventName, handler);
				return () => document.body.removeEventListener(eventName, handler);
			});
			// detach all handlers in one call with intact refs
			if (!this.hamburgerIsToggled) {
				bodyClick.forEach(d => d());
			}
		}
	}

	$eventBusSub: Subscription;

	constructor(private idService: IdService, private sidenavService: SidenavService) {}

	ngOnInit() {
		this.id = this.id ? this.id : this.idService.generate();
		this.mountEventBus();
		this.sidenavService.setParent(this);
	}

	ngAfterContentInit(): void {
		// cleanup view
		if (!this.asElement(this.mobileTopSlot.nativeElement).children.length && !this.search) {
			this.asElement(this.mobileTopSlot.nativeElement).remove();
		}
		if (!this.asElement(this.otherSlot.nativeElement).children.length) {
			this.asElement(this.otherSlot.nativeElement).remove();
		}
		if (!this.asElement(this.mobileBottomSlot.nativeElement).children.length) {
			this.asElement(this.mobileBottomSlot.nativeElement).remove();
		}

		// get the corresponding sidenav item to a provided currentPage object
		this.getCurrentPageKey();
	}

	mountEventBus() {
		this.$eventBusSub = this.sidenavService.$eventBus.subscribe(event => {
			if (event.isDirectChild && event.activeItemId && this.sidenavService.itemsFlat[event.activeItemId]) {
				// set the new active link or expand by broadcasting new active to all subscribers
				this.sidenavService.$activeItemId.next(event.activeItemId);
			}

			const isMd = window.matchMedia('(max-width: 991px)').matches;
			// ensure that the nav re-hides when link is selected on smaller screens
			// ...or if expand is selected on larger screens
			if (
				(!event.item.expandable && event.item.router && isMd) ||
				(event.item.expandable && this.hamburgerIsToggled && !isMd)
			) {
				this.toggleLinks(false);
				// pass nav toggle state up to parent
				this.navtoggleupdate.emit({ isToggled: false, self: this });
			}
		});
	}

	asElement(element: Element): HTMLElement {
		return element as HTMLElement;
	}

	ngAfterViewInit() {
		// add classes to the body to properly style content and top nav
		// add default side nav class to body
		if (this.toggle) {
			const navClass = this.toggle === 'icon' ? 'nav-collapsable-icon' : 'nav-collapsable';
			document.body.classList.add(navClass);
			document.body.classList.add('nav-is-default');
		}

		// add right nav class if the hamburger is to the right
		if (this.hamburgerPosition === 'right') {
			document.body.classList.add('nav-right');
		}

		if (!this.showHamburger) {
			document.body.classList.add('nav-no-hamburger');
		}

		this.navtoggleupdate.emit({ isToggled: this.hamburgerIsToggled, self: this });
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes) {
			if (changes.currentPage) {
				this.getCurrentPageKey();
			}
		}
	}

	ngOnDestroy() {
		if (this.idService) {
			this.idService.remove(this.id);
		}
		if (this.$eventBusSub) {
			this.$eventBusSub.unsubscribe();
		}
	}

	toggleLinks(toggled: boolean) {
		this.hamburgerIsToggled = toggled === true || toggled === false ? toggled : !this.hamburgerIsToggled;
		const body = document.body;

		// add / remove classes to the body depending if nav is open or closed
		if (this.hamburgerIsToggled) {
			body.classList.add('nav-is-toggled');
			if (this.toggle) {
				body.classList.remove('nav-is-default');
			}
		} else {
			body.classList.remove('nav-is-toggled');
			if (this.toggle) {
				body.classList.add('nav-is-default');
			}
		}

		const isMd = window.matchMedia('(max-width: 991px)').matches;

		// close all Expandables if the nav is toggled on larger screens
		if (this.hamburgerIsToggled && !isMd && this.toggle === 'icon') {
			this.sidenavService.closeAllItems();
		}

		// let everyone know the nav toggle state
		this.navtoggleupdate.emit({ isToggled: this.hamburgerIsToggled, self: this });
	}

	toggleLinksMobile(e) {
		if (e.target.classList.contains('nav-is-toggled')) {
			this.toggleLinks(false);
		}
	}

	emitSearch(searchEvent: IInputTextUpdateEvent) {
		this.onSearch.emit(searchEvent);
	}

	setActiveExpand(itemId: string) {
		// set the active.expand if the link is an expandable
		if (itemId && this.sidenavService.itemsFlat[itemId].expandable) {
			this.sidenavService.$activeItemId.next(itemId);
		}
	}

	getCurrentPageKey() {
		// get and save the id of the current page expandable
		if (this.currentPage) {
			const currentPageKey = this.sidenavService.findMatchingItemForRoute(this.currentPage);
			// NOTE: only the first matched segment is used, which means the highest expandable item is automatically expanded
			this.setActiveExpand(currentPageKey);
		}
	}
}
